import React from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Icon,
  Text,
  VStack,
  HStack,
  Tag,
  useBreakpointValue
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile } from 'react-icons/fi';

export const MAX_GPX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const ACCENT_RGB = 'var(--chakra-colors-brand-primary-rgb)';

interface FileDropzoneProps {
  selectedFile: File | null;
  onDrop: (acceptedFiles: File[]) => void;
  onDropRejected?: (rejections: FileRejection[]) => void;
}

const FileDropzone = ({ selectedFile, onDrop, onDropRejected }: FileDropzoneProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    maxSize: MAX_GPX_FILE_SIZE_BYTES,
    accept: { 'application/gpx+xml': ['.gpx'] },
    onDrop,
    onDropRejected,
    noClick: false,
    noKeyboard: false,
  });

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <Box
        {...getRootProps()}
        data-testid="file-dropzone"
        p={{ base: 7, md: 10 }}
        borderWidth="2px"
        borderRadius="12px"
        borderStyle="dashed"
        borderColor={isDragActive ? 'brand.primary' : 'border.subtle'}
        bg="bg.canvas"
        boxShadow={isDragActive ? `0 0 0 4px rgba(${ACCENT_RGB}, 0.12)` : 'none'}
        transition="all 0.25s ease"
        _hover={{
          borderColor: 'brand.primary',
          boxShadow: `0 0 0 4px rgba(${ACCENT_RGB}, 0.08)`,
        }}
        aria-describedby="dropzone-description"
        role="button"
        tabIndex={0}
        cursor="pointer"
      >
        <input {...getInputProps()} data-testid="file-input" />
        <VStack spacing={4}>
          <Box
            height={{ base: "80px", md: "120px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AnimatePresence mode="wait">
              {selectedFile ? (
                <motion.div
                  key="file-selected"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                >
                  <VStack spacing={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w={14}
                      h={14}
                      borderRadius="10px"
                      bg={`rgba(${ACCENT_RGB}, 0.10)`}
                    >
                      <Icon as={FiFile} w={7} h={7} color="brand.primary" />
                    </Box>
                    <Text fontWeight="600" fontSize="sm">{selectedFile.name}</Text>
                    <Text fontSize="xs" color="text.muted">
                      {selectedFile.size >= 1024 * 1024
                        ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                        : `${(selectedFile.size / 1024).toFixed(2)} KB`}
                    </Text>
                  </VStack>
                </motion.div>
              ) : (
                <motion.div
                  key="no-file"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                >
                  <VStack spacing={3}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w={{ base: 14, md: 16 }}
                      h={{ base: 14, md: 16 }}
                      borderRadius="12px"
                      bg={isDragActive ? `rgba(${ACCENT_RGB}, 0.12)` : 'bg.subtleTint'}
                      transition="all 0.2s ease"
                    >
                      <Icon
                        as={FiUploadCloud}
                        w={{ base: 7, md: 8 }}
                        h={{ base: 7, md: 8 }}
                        color={isDragActive ? 'brand.primary' : 'text.muted'}
                        transition="color 0.2s ease"
                      />
                    </Box>
                    <VStack spacing={1}>
                      <Text fontWeight="600" fontSize={{ base: 'sm', md: 'md' }}>
                        {isMobile
                          ? 'Tap to select your GPX file'
                          : (isDragActive ? 'Drop your GPX file here' : 'Drag & drop your GPX file here')}
                      </Text>
                      {!isMobile && (
                        <Text fontSize="sm" color="text.muted">
                          or click to browse files
                        </Text>
                      )}
                    </VStack>
                    <HStack spacing={2} mt={1}>
                      <Tag
                        size="sm"
                        borderRadius="6px"
                        fontFamily="monospace"
                        fontSize="xs"
                        letterSpacing="0.05em"
                        bg={`rgba(${ACCENT_RGB}, 0.10)`}
                        color="brand.primary"
                        px={2}
                        py={0.5}
                      >
                        .gpx
                      </Tag>
                      <Text fontSize="xs" color="text.muted">
                        GPS Exchange Format
                      </Text>
                    </HStack>
                  </VStack>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </VStack>
      </Box>
    </motion.div>
  );
};

export default FileDropzone;
